import {Component, OnInit, signal, ChangeDetectorRef, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {ActivatedRoute, Router} from '@angular/router';
import {Toast} from 'primeng/toast';
import {MessageService} from 'primeng/api';
import {TranslatePipe} from '@ngx-translate/core';
import {RegionService} from '@6grain/masofa-monolith-api-client/dictionaries/api/region.service';
import {PrimeNG} from 'primeng/config';
import {BidService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/bid.service';
import {SeasonService} from '@6grain/masofa-monolith-api-client/crop-monitoring/api/season.service';
import {Bid, FieldService} from "@6grain/masofa-monolith-api-client/crop-monitoring";
import {TranslateMessageService} from "@services/translate-message";
import {GroundSurvey} from "@app/pages/crop-monitoring/components/request-add-page/forms/ground-surver/ground-survey";
import {BackButton} from "@components/ui/back-button/back-button";
import {ButtonDirective} from "primeng/button";
import {DialogComponent} from "@components/dialog";
import {Uav} from "@app/pages/crop-monitoring/components/request-add-page/forms/uav/uav";
import {Loading} from '@components/ui/loading/loading';
import {BidStateType} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidStateType';
import {BidUpdateViewModel} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidUpdateViewModel';
import {BidCreateViewModel} from '@6grain/masofa-monolith-api-client/crop-monitoring/model/bidCreateViewModel';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {areBidPlantingFieldsOptional, resolveBidWorkType} from '@shared/data/bid-work-type.util';
import {BidsToSeasonService} from '@services/bids-to-season.service';
import {toBidCreateFromSeasonPayload} from '@shared/data/bid-create-from-season.util';
import {EMPTY, Observable, of, Subject} from 'rxjs';
import {catchError, map, switchMap, take, takeUntil} from 'rxjs/operators';

@Component({
  selector: 'app-request-add-page',
  standalone: true,
  imports: [
    CommonModule,
    BackButton,
    GroundSurvey,
    Toast,
    ButtonDirective,
    DialogComponent,
    TranslatePipe,
    Uav,
    Loading,
  ],
  providers: [
    {provide: MessageService, useClass: TranslateMessageService}
  ],

  templateUrl: './request-add-page.html',
  styleUrl: './request-add-page.scss'
})
export class RequestAddPageComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();


  requestType = signal<'ground' | 'limiting'>('ground');
  isMapMode = signal(false);
  isSaving = signal(false);
  isCanceling = signal(false);
  isLoading = signal(false);

  bid: Bid | any = {}
  showConfirmCancelModal: boolean = false;
  /** SeasonId from GET — CustomUpdate cannot replace it; parent-anomaly autofill must not overwrite. */
  private loadedSeasonId: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    public regionService: RegionService,
    private bidService: BidService,
    private fieldService: FieldService,
    private seasonService: SeasonService,
    private messageService: MessageService,
    public primeng: PrimeNG,
    private cdr: ChangeDetectorRef,
    private bidsToSeason: BidsToSeasonService,
  ) {
  }

  ngOnInit() {


    // Check if we're in edit mode
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.loadRequestForEdit(params['id']);
      } else {
        setTimeout(() => {

          this.cdr.detectChanges();
        }, 100);
      }
    });

    this.route.queryParams.subscribe(params => {
      if (params['type'] === 'limiting') {
        this.requestType.set('limiting');
        this.isMapMode.set(true);
      } else {
        this.isMapMode.set(true);
      }
      if (params['fieldId']) {
        this.bid.fieldId = params['fieldId'];
        this.loadBidFromField();
      }
    });
  }

  saveRequest(formData?: Bid | Record<string, unknown>) {
    if (formData) {
      this.bid = {...this.bid, ...formData};
    }
    this.isSaving.set(true);
    if (this.bid?.id) {
      this.bidService.cropMonitoringBidCustomUpdatePut(this.createBidPayload()).subscribe({
        next: (result) => {
          this.requestMessage('success', 'Success', 'requestUpdatedSuccessfully');
          this.isSaving.set(false);
          // setTimeout(() => this.backToList(), 150);
        },
        error: (httpError:HttpErrorResponse) => {
          console.error('Error updating request:', httpError);
          this.requestMessage('error', 'Error', this.resolveErrorDetail(httpError));
          this.isSaving.set(false);
        }
      });
    } else {
      // Create — season path when BidsToSeason flag ON; legacy crop path only when flag OFF
      this.bidsToSeason
        .isEnabled()
        .pipe(
          take(1),
          switchMap((enabled) => {
            const workType = resolveBidWorkType(this.bid?.bidWorkType);
            const plantingFieldsOptional = areBidPlantingFieldsOptional(workType);

            if (enabled && this.bid?.seasonId) {
              return this.bidsToSeason.customCreateFromSeason(
                toBidCreateFromSeasonPayload(this.bid),
              );
            }

            // WT10 / WT15: season/crop optional — create without season via CustomCreate.
            if (enabled && !this.bid?.seasonId && !plantingFieldsOptional) {
              this.requestMessage('error', 'Error', 'SeasonIsRequired');
              this.isSaving.set(false);
              return EMPTY;
            }

            return this.bidService.cropMonitoringBidCustomCreatePost(
              this.createBidCreatePayload(),
            );
          }),
        )
        .subscribe({
          next: (id) => {
            this.requestMessage('success', 'Success', 'requestCreatedSuccessfully');
            this.isSaving.set(false);

            if (id) {
              this.router.navigate(['/crop-monitoring/requests/edit/' + id]);
            }
          },
          error: (httpError: HttpErrorResponse) => {
            console.error('Error creating request:', httpError);
            this.requestMessage('error', 'Error', this.resolveErrorDetail(httpError));
            this.isSaving.set(false);
          },
        });
    }
  }


  cancelBid() {
    // this.backToList();
    const cancelParam: any = {
      ...this.createBidPayload(),
      bidState: BidStateType.NUMBER_5
    }

    this.isCanceling.set(true);

    this.bidService.cropMonitoringBidUpdatePut(cancelParam).subscribe({
      next: () => {
        this.requestMessage('success', 'Success', 'requestCancelSuccessfully');
        this.isCanceling.set(false);
        setTimeout(() => this.backToList(), 150);
      },
      error: (error) => {
        console.error('Error updating request:', error);
        // this.requestMessage('error', 'Error', 'requestCancelFailed');
        this.isCanceling.set(false);
      }
    });
  }

  backToList() {
    this.router.navigate(['/crop-monitoring/requests']);
  }

  private resolveErrorDetail(error: HttpErrorResponse): string {
    if (typeof error.error === 'string') {
      return error.error;
    }

    return error.error?.message ?? error.message ?? 'requestCreationFailed';
  }

  requestMessage(severity: string, summary: string, detail: string): void {
    this.messageService.add({
      severity,
      summary,
      detail,
      life: 3000,
    });
  }

  loadRequestForEdit(id: string) {
    this.isLoading.set(true);
    this.bidService
      .cropMonitoringBidGetByIdIdGet(id)
      .pipe(switchMap((bid) => this.resolveBidFieldIdForEdit(bid)), takeUntil(this.destroy$))
      .subscribe({
        next: (bid) => {
          this.loadedSeasonId = bid.seasonId ?? null;
          this.bid = bid;
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        },
      });
  }

  /** Season-linked bids may store placement on season only (fieldId null). */
  private resolveBidFieldIdForEdit(bid: Bid): Observable<Bid> {
    const seasonId = bid.seasonId ?? null;
    if (bid.fieldId || !seasonId) {
      return of(bid);
    }

    return this.seasonService.cropMonitoringSeasonGetByIdIdGet(seasonId).pipe(
      map((season) => ({
        ...bid,
        fieldId: season.fieldId ?? bid.fieldId ?? null,
      })),
      catchError(() => of(bid)),
    );
  }


  private createBidPayload(): BidUpdateViewModel {
    const cloneBid: BidUpdateViewModel = {
      parentId: this.bid.parentId,
      bidTypeId: this.bid.bidTypeId,
      foremanId: this.bid.foremanId,
      workerId: this.bid.workerId,
      startDate: this.bid.startDate,
      deadlineDate: this.bid.deadlineDate,
      endDate: this.bid.endDate,
      fieldId: this.bid.fieldId,
      seasonId: this.bid?.id ? this.loadedSeasonId : (this.bid.seasonId ?? null),
      regionId: this.bid.regionId,
      cropId: this.bid.cropId,
      varietyId: this.bid.varietyId,
      comment: this.bid.comment,
      description: this.bid.description,
      lat: this.bid.lat,
      lng: this.bid.lng,
      number: this.bid.number,
      fieldPlantingDate: this.bid.fieldPlantingDate,
      customer: this.bid.customer,
      fileResultId: this.bid.fileResultId,
      bidTemplateId: this.bid.bidTemplateId,
      isUnvalidBid: this.bid.isUnvalidBid,
      bidState: this.getBidState,
      status: this.bid.status,
      polygon: this.bid.polygon as any,
      publish: this.bid.publish,
      archive: this.bid.archive,
      bidWorkType: resolveBidWorkType(this.bid.bidWorkType),
    } as BidUpdateViewModel & { bidWorkType?: number };

    if (this.bid?.id) {
      cloneBid['id'] = this.bid.id;
    }

    this.bid['archive'] = cloneBid.archive;
    this.bid['publish'] = cloneBid.publish;

    return cloneBid;
  }

  /** Create DTO (2.19.9+) includes anomalyPolygonId; update DTO does not. */
  private createBidCreatePayload(): BidCreateViewModel {
    return {
      ...this.createBidPayload(),
      anomalyPolygonId: this.bid.anomalyPolygonId ?? null,
    };
  }

  get getBidState(): BidStateType {

    if (this.bid.bidState !== BidStateType.NUMBER_5) {
      if (this.bid?.publish && !this.bid?.archive) {
        return BidStateType.NUMBER_1;
      }
    }
    return this.bid?.bidState;

  }

  loadBidFromField() {
    if (this.bid?.fieldId) {
      this.isLoading.set(true)
      this.fieldService.cropMonitoringFieldGetByIdIdGet(this.bid.fieldId).pipe(takeUntil(this.destroy$)).subscribe({
        next: (field) => {
          if (field.centerY){
            this.bid.lat = field.centerY;
          }
          if (field.centerX){
            this.bid.lng = field.centerX;
          }
          this.bid.regionId = field.regionId;
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
        }
      });
    }

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
